import * as _ from 'lodash';
import makeComp from '../../../util/profiler';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import {ResolverContext} from './resolverContext';
import * as Graph from './graph';
import * as HL from './hl';
import * as Listener from './client';
import * as ServerApiTest from './serverApiTest';
import * as ServerApiProd from './serverApiProd';
import {useApolloClient, useDeepMemo} from '../../../state/hooks';
import * as Op from './ops';
import * as Types from '../types';

interface ClientState {
  client?: Listener.Client;
}

const ClientContext = React.createContext<ClientState>({
  client: undefined,
});

export const ComputeGraphContextProvider: React.FC<{test?: boolean}> = makeComp(
  ({test, children}) => {
    const apolloClient = useApolloClient();
    const context = useMemo(() => {
      return {
        client: new Listener.Client({
          backend: test
            ? new ServerApiTest.Client()
            : new ServerApiProd.Client(apolloClient),
        }),
      };
    }, [test, apolloClient]);

    return (
      <ClientContext.Provider value={context}>
        {children}
      </ClientContext.Provider>
    );
  },
  {id: 'PanelExportContextProvider', memo: true}
);

export const useNodeValue = <T extends Types.Type>(
  node: Graph.NodeOrVoidNode<T>
): {loading: boolean; result: Types.TypeToTSTypeInner<T>} => {
  // Note this is probably expensive and we do it way too
  // often. TODO: check perf!
  node = useDeepMemo(node);
  const context = useContext(ClientContext);
  const [result, setResult] = useState<{
    node: Graph.NodeOrVoidNode;
    value: any;
  }>({node: Graph.voidNode(), value: undefined});
  const client = context.client;
  if (client == null) {
    throw new Error('client not initialized!');
  }
  useEffect(() => {
    if (!Graph.isVoidNode(node)) {
      const obs = client.subscribe(node);
      const sub = obs.subscribe(nodeRes => {
        setResult({node, value: nodeRes});
      });
      return () => sub.unsubscribe();
    } else {
      return () => {};
    }
  }, [client, node]);

  const finalResult = useMemo(() => {
    return {
      loading: node !== result.node,
      result: result.value,
    };
  }, [result, node]);
  return finalResult;
};

export const useContextBound = <T extends any[], R>(
  fn: (context: ResolverContext, ...rest: T) => R
): ((...args: T) => R) => {
  const context = useContext(ClientContext).client?.context;
  if (context == null) {
    throw new Error('CG context not initialized');
  }
  return useCallback((...args: T) => fn(context, ...args), [context, fn]);
};

// Given an array node, return a set of valid nodes, one for
// each item in the array.
// TODO: in the future it would be cool to move this logic down,
//   it doesn't need to depend on react hooks.
export const useEach = (
  node: Graph.Node<{type: 'array'; objectType: 'any'}>
) => {
  const countNode = useMemo(() => Op.opCount({arr: node}), [node]);
  const countValue = useNodeValue(countNode);
  const result = useMemo(
    () =>
      _.range(countValue.result).map(i =>
        Op.opIndex({arr: node, index: Op.constNumber(i)})
      ),

    [countValue.result, node]
  );
  const finalResult = useMemo(() => {
    return {
      loading: countValue.loading,
      result,
    };
  }, [countValue.loading, result]);

  return finalResult;
};

export const useNodeWithServerType = (
  node: Graph.Node
): {loading: boolean; result: Graph.Node} => {
  node = useDeepMemo(node);
  if (node.nodeType !== 'output') {
    throw new Error('invalid');
  }
  const [result, setResult] = useState<{
    node: Graph.NodeOrVoidNode;
    value: any;
  }>({node: Graph.voidNode(), value: undefined});
  const context = useContext(ClientContext);
  useEffect(() => {
    // TODO: This is a race if we have multiple loading in parallel!
    HL.refineNodeType(context.client?.context!, node).then(newNode => {
      setResult({node, value: newNode});
    });
  }, [context, node]);
  const finalResult = useMemo(() => {
    return {
      loading: node !== result.node,
      result: node === result.node ? result.value : node,
    };
  }, [result, node]);
  return finalResult;
};
